import { useCallback, useEffect, useRef } from "react";

/** Pixels from the left/right edge where auto-scroll starts (Jira-style). */
const EDGE_ZONE_PX = 80;
/** Max horizontal scroll speed in px per animation frame. */
const MAX_SPEED_PX = 22;

/**
 * While dragging a Kanban card, scroll the board horizontally when the pointer
 * nears the left or right edge — same behaviour as Jira.
 */
export function useKanbanDragAutoScroll(isDragging: boolean) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const speedRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  const stopAutoScroll = useCallback(() => {
    speedRef.current = 0;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    const el = scrollRef.current;
    const speed = speedRef.current;
    if (!el || speed === 0) {
      rafRef.current = null;
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      stopAutoScroll();
      return;
    }

    const next = Math.max(0, Math.min(maxScroll, el.scrollLeft + speed));
    el.scrollLeft = next;

    if (
      (speed < 0 && next <= 0) ||
      (speed > 0 && next >= maxScroll)
    ) {
      stopAutoScroll();
      return;
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [stopAutoScroll]);

  const updateScrollFromPointer = useCallback(
    (clientX: number) => {
      const el = scrollRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const x = clientX - rect.left;
      const width = rect.width;

      let speed = 0;
      if (x < EDGE_ZONE_PX) {
        const intensity = (EDGE_ZONE_PX - x) / EDGE_ZONE_PX;
        speed = -intensity * MAX_SPEED_PX;
      } else if (x > width - EDGE_ZONE_PX) {
        const intensity = (EDGE_ZONE_PX - (width - x)) / EDGE_ZONE_PX;
        speed = intensity * MAX_SPEED_PX;
      }

      speedRef.current = speed;

      if (speed !== 0 && rafRef.current === null) {
        rafRef.current = requestAnimationFrame(tick);
      }
      if (speed === 0) {
        stopAutoScroll();
      }
    },
    [stopAutoScroll, tick]
  );

  useEffect(() => {
    if (!isDragging) {
      stopAutoScroll();
      return;
    }

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      updateScrollFromPointer(e.clientX);
    };

    document.addEventListener("dragover", onDragOver);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      stopAutoScroll();
    };
  }, [isDragging, stopAutoScroll, updateScrollFromPointer]);

  return { scrollRef };
}
